import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useReveal } from "../../hooks/useReveal";
import { ParallaxBg } from "../../components/ParallaxBg";
import { OFFER_STACK, METHOD_STEPS } from "../../lib/replayCopy";

gsap.registerPlugin(ScrollTrigger);

export const scrollToBook = () => {
  const el = document.getElementById("book");
  if (window.__lenis) window.__lenis.scrollTo(el, { duration: 1.4 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

const LINES = [
  { text: "YOU'VE SEEN HOW TO USE", style: {} },
  { text: "YOUR VA LOAN", style: { color: "#3D7BFF" } },
  { text: "NOW PUT IT", style: { opacity: 0.28 } },
  { text: "TO YOUR ADVANTAGE.", style: {} },
];

export function RManifesto() {
  const ref = useRef(null);
  useEffect(() => {
    const words = ref.current.querySelectorAll(".manifesto-word");
    const tween = gsap.to(words, {
      opacity: 1, y: 0, duration: 0.7, ease: "power2.out", stagger: 0.04,
      scrollTrigger: { trigger: ref.current, start: "top 72%" },
    });
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);
  return (
    <section id="method" ref={ref} className="relative overflow-hidden py-28 md:py-40" data-testid="replay-manifesto-section">
      <ParallaxBg image="/bg/home4.jpg" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-[1fr_auto] gap-16 items-start">
        <div>
          <p className="section-label mb-10">[001] — WHAT YOU JUST LEARNED</p>
          <h2 className="manifesto-statement mb-10" style={{ fontSize: "clamp(36px, 4.8vw, 64px)" }} data-testid="replay-manifesto-statement">
            {LINES.map((line, li) => (
              <span key={li} className="block">
                {line.text.split(" ").map((w, wi) => (
                  <span key={wi} className="manifesto-word mr-[0.22em]" style={line.style}>{w}</span>
                ))}
              </span>
            ))}
          </h2>
          <p style={{ color: "rgba(240,236,228,0.55)", fontSize: 16, lineHeight: 1.75, maxWidth: 560 }} className="mb-10">
            You saw it in the training: understanding your VA benefit — your entitlement, the funding fee and possible exemptions, realistic closing costs, and how seller concessions may reduce what you bring to closing — changes how you approach your entire purchase. Just as important is coordination: a Mortgage Loan Advisor who communicates clearly with your Realtor so financing issues are anticipated, not discovered. Your free strategy call is where we apply all of it to your specific situation — your eligibility and entitlement in plain English, your numbers, and a written plan you can act on.
          </p>
          <button className="btn-outline" data-testid="replay-manifesto-cta" onClick={scrollToBook}>SEE HOW IT APPLIES TO YOU →</button>
        </div>
        <div className="pixel-grid hidden lg:grid mt-24">
          {Array.from({ length: 64 }).map((_, i) => <span key={i} />)}
        </div>
      </div>
    </section>
  );
}

export function RMethod() {
  const ref = useReveal();
  return (
    <section ref={ref} className="border-y" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="replay-method-section">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-3">
        {METHOD_STEPS.map((step, i) => (
          <div key={i} className="roadmap-col reveal-up" data-testid={`method-step-${i + 1}`}>
            <span className="roadmap-num">{step.num}</span>
            <p className="eyebrow mb-4">THE WHOLE-DEAL METHOD · {step.num}</p>
            <h3 className="font-black uppercase text-2xl mb-4">{step.name}</h3>
            <p className="text-sm" style={{ color: "var(--muted)", lineHeight: 1.7 }}>{step.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ROfferStack() {
  const ref = useReveal();
  return (
    <section ref={ref} className="relative overflow-hidden py-28 md:py-36" data-testid="offer-stack-section">
      <ParallaxBg image="/bg/home5.jpg" />
      <div className="relative max-w-[860px] mx-auto px-6">
        <p className="section-label mb-8 reveal-up">[002] — THE OFFER</p>
        <h2 className="reveal-up font-black uppercase mb-14" style={{ fontSize: "clamp(28px, 3.6vw, 48px)", letterSpacing: "-0.02em" }}>
          HERE'S WHAT'S WAITING WHEN YOU BOOK
        </h2>
        <div className="glass-card p-7 md:p-10 reveal-up" data-testid="offer-stack-card">
          <ul className="space-y-5 mb-8">
            {OFFER_STACK.map((o, i) => (
              <li key={i} className="reveal-up flex gap-4 items-start justify-between" data-testid={`offer-item-${i + 1}`}>
                <span className="flex gap-3 items-start text-sm md:text-base" style={{ color: "rgba(240,236,228,0.85)" }}>
                  <span style={{ color: "#3D7BFF", fontWeight: 800 }}>✓</span>
                  {o.item}
                </span>
                <span className="mono flex-shrink-0" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>Value {o.value}</span>
              </li>
            ))}
          </ul>
          <div className="pt-6 border-t" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <p className="mono mb-3" style={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
              <s>$3,400 real value</s>
            </p>
            <p className="font-extrabold text-lg mb-8" data-testid="offer-reveal-line">
              Your investment today: <span style={{ color: "#3D7BFF" }}>$0</span> — just ~90 minutes and your honesty.
            </p>
            <button className="btn-accent w-full" data-testid="offer-stack-cta" onClick={scrollToBook}>✓ BOOK MY FREE STRATEGY CALL</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RGuarantee() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-28 md:py-36" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="guarantee-section">
      <div className="max-w-[760px] mx-auto px-6 text-center">
        <p className="section-label mb-12 reveal-up justify-center">[003] — THE GUARANTEE</p>
        <div className="reveal-up guarantee-card" data-testid="guarantee-card">
          <i className="fa-solid fa-shield-halved mb-6" style={{ color: "#3D7BFF", fontSize: 30 }}></i>
          <h2 className="font-black uppercase mb-6" style={{ fontSize: "clamp(22px, 2.6vw, 32px)", letterSpacing: "-0.01em" }}>
            THE VA HOME-READY GAME PLAN GUARANTEE
          </h2>
          <p style={{ color: "rgba(240,236,228,0.7)", fontSize: 16, lineHeight: 1.8, fontStyle: "italic" }}>
            "Complete your call and if you don't walk away with a written plan you can act on, I'll keep working with you until you do."
          </p>
        </div>
        <p className="reveal-up mt-6" style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }} data-testid="guarantee-small-print">
          Service guarantee only. Not a guarantee of loan approval, rate, or closing.
        </p>
      </div>
    </section>
  );
}
